import React, { lazy, Suspense, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import {
  getBlogPosts,
  deleteBlogPost,
  addBlogPost,
  updateBlogPost,
} from "../utils/blogStorage";
import { getInquiries } from "../utils/inquiryStorage";
import {
  Plus,
  Edit2,
  Trash2,
  LogOut,
  ArrowLeft,
  Calendar,
  Clock,
  MessageSquare,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BlogForm = lazy(() => import("../components/BlogForm"));

const AdminBlog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [inquiryCount, setInquiryCount] = useState(0);

  const loadInquiryCount = async () => {
    try {
      const inquiries = await getInquiries();
      const newCount = inquiries.filter((i) => i.status === "new").length;
      setInquiryCount(newCount);
    } catch (error) {
      console.error("Error loading inquiry count:", error);
      setInquiryCount(0);
    }
  };

  const loadPosts = useCallback(async () => {
    try {
      const loadedPosts = await getBlogPosts();
      setPosts(loadedPosts);
    } catch (error) {
      console.error("Error loading blog posts:", error);
      toast({
        title: "Unable to Load Posts",
        description: "Please check your Supabase setup and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    loadPosts();
    loadInquiryCount();

    if (location.state?.editingPost) {
      setEditingPost(location.state.editingPost);
      setIsFormOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [user, navigate, location, loadPosts]);

  const handleAddPost = () => {
    setEditingPost(null);
    setIsFormOpen(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      try {
        await deleteBlogPost(id);
        await loadPosts();
        toast({
          title: "Post Deleted",
          description: "Blog post has been deleted successfully.",
        });
      } catch (error) {
        console.error("Error deleting blog post:", error);
        toast({
          title: "Delete Failed",
          description: "The blog post could not be deleted.",
          variant: "destructive",
        });
      }
    }
  };

  const handleSave = async (postData) => {
    try {
      if (editingPost) {
        const updatedPost = await updateBlogPost(editingPost.id, postData);
        setPosts((prev) =>
          prev.map((p) => (p.id === updatedPost.id ? updatedPost : p))
        );
        toast({
          title: "Post Updated",
          description: "Blog post has been updated successfully.",
        });
      } else {
        const newPost = await addBlogPost(postData);
        setPosts((prev) => [newPost, ...prev]);
        toast({
          title: "Post Created",
          description: "Blog post has been created successfully.",
        });
      }
      setIsFormOpen(false);
      setEditingPost(null);
    } catch (error) {
      console.error("Error saving blog post:", error);
      const duplicateSlug =
        error?.code === "23505" || /duplicate/i.test(error?.message || "");

      toast({
        title: duplicateSlug ? "Slug Already Exists" : "Save Failed",
        description: duplicateSlug
          ? "Please change the slug and try again."
          : "The blog post could not be saved.",
        variant: "destructive",
      });
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingPost(null);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (isFormOpen) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Suspense
            fallback={
              <div className="bg-white rounded-2xl shadow-xl p-8 text-gray-600">
                Loading editor...
              </div>
            }
          >
            <BlogForm
              initialData={editingPost}
              onSubmit={handleSave}
              onCancel={handleFormCancel}
            />
          </Suspense>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Blog Management
                </h1>
                <p className="text-sm text-gray-600">
                  Welcome, {user?.name || user?.username}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/admin/inquiries")}
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 font-semibold rounded-lg hover:bg-blue-200 transition-all relative"
              >
                <MessageSquare className="w-5 h-5" />
                Inquiries
                {inquiryCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {inquiryCount}
                  </span>
                )}
              </button>
              <button
                onClick={handleAddPost}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                <Plus className="w-5 h-5" />
                New Post
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-3xl font-bold text-purple-600 mb-2">
              {posts.length}
            </h3>
            <p className="text-gray-600">Total Posts</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-3xl font-bold text-pink-600 mb-2">
              {posts.filter((p) => p.category === "Textile Dyes").length}
            </h3>
            <p className="text-gray-600">Textile Dyes</p>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-3xl font-bold text-indigo-600 mb-2">
              {new Set(posts.map((p) => p.category)).size}
            </h3>
            <p className="text-gray-600">Categories</p>
          </div>
        </div>

        {/* Posts List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">All Posts</h2>
          </div>

          {posts.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-600 mb-4">No blog posts yet.</p>
              <button
                onClick={handleAddPost}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                <Plus className="w-5 h-5" />
                Create First Post
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {posts.map((post) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                          {post.category}
                        </span>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(post.date)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 line-clamp-2">{post.excerpt}</p>
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {post.tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditPost(post)}
                        className="p-2 hover:bg-purple-100 text-purple-600 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBlog;
